import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import AdminLayout from '../Admin/components/AdminLayout'
import CoordinatorLayout from '../Coordinator/components/CoordinatorLayout'
import SuperAdminLayout from '../SuperAdmin/components/SuperAdminLayout'
import jsQR from 'jsqr'

// Pulled outside so refs never get remounted on re-render
function ScanContent({ scanning, error, manualId, setManualId, startScan, stopScan, handleManualLookup, videoRef, canvasRef }) {
  return (
    <>
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">
          Scan Member Card
        </h1>
        <p className="text-gray-500 text-sm">Verify a supporter's membership by scanning their QR code</p>
      </div>

      <div className="max-w-md flex flex-col gap-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-white/10">
            <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Camera Scanner</div>
          </div>
          <div className="p-4">
            {/* Always rendered so refs are always attached to real DOM nodes */}
            <div
              className="relative w-full overflow-hidden bg-black"
              style={{ aspectRatio: '1/1', display: scanning ? 'block' : 'none' }}
            >
              <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 border-2 border-green-400 relative">
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-green-400" />
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-green-400" />
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-green-400" />
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-green-400" />
                </div>
              </div>
            </div>
            <canvas ref={canvasRef} className="hidden" />

            {!scanning ? (
              <div className="text-center py-8">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-gray-400 mx-auto mb-4">
                  <path d="M3 3h5v5H3zM16 3h5v5h-5zM3 16h5v5H3zM16 16h2v2h-2zM20 16h1v1h-1zM16 20h1v1h-1zM20 20h1v1h-1zM20 18h1v1h-1zM18 20h1v1h-1z"/>
                </svg>
                <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                  Point your camera at the QR code on a member's digital card
                </p>
                <button
                  onClick={startScan}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 font-bold uppercase tracking-widest text-sm transition-colors w-full"
                >
                  Start Camera
                </button>
              </div>
            ) : (
              <button
                onClick={stopScan}
                className="w-full mt-4 border border-gray-200 dark:border-white/10 hover:border-red-500 text-gray-500 dark:text-gray-400 hover:text-red-500 py-3 font-bold uppercase tracking-widest text-sm transition-colors"
              >
                Stop Scanning
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-white/10">
            <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Manual Lookup</div>
          </div>
          <div className="p-4">
            <p className="text-gray-500 text-xs mb-4">Enter a membership ID manually if camera is unavailable</p>
            <form onSubmit={handleManualLookup} className="flex gap-2">
              <input
                type="text"
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                placeholder="e.g. MMW-2025-1234"
                className="flex-1 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-gray-400"
              />
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white px-5 py-3 font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap"
              >
                Look Up
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default function AdminScan() {
  const { user } = useAuth()
  const [role, setRole] = useState(null)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState(null)
  const [manualId, setManualId] = useState('')
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const animRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchRole = async () => {
      if (!user) return
      const { data } = await supabase
        .from('supporters').select('role').eq('id', user.id).single()
      if (data) setRole(data.role)
    }
    fetchRole()
  }, [user])

  const startScan = async () => {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      })
      streamRef.current = stream

      // Wait for video element to be ready before assigning
      if (!videoRef.current) {
        stream.getTracks().forEach(t => t.stop())
        setError('Camera could not attach to video element. Please try again.')
        return
      }

      videoRef.current.srcObject = stream

      // Use oncanplay to ensure video is truly ready before playing
      videoRef.current.oncanplay = () => {
        videoRef.current.play()
        setScanning(true)
      }
    } catch (err) {
      setError('Camera access denied. Please allow camera access in your browser settings.')
    }
  }

  const stopScan = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (animRef.current) {
      cancelAnimationFrame(animRef.current)
      animRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
      videoRef.current.oncanplay = null
    }
    setScanning(false)
  }

  useEffect(() => {
    if (!scanning) return

    const canvas = canvasRef.current
    const video = videoRef.current

    if (!canvas || !video) return

    const ctx = canvas.getContext('2d')

    const tick = () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const code = jsQR(imageData.data, imageData.width, imageData.height)
        if (code) {
          stopScan()
          let membershipId = code.data
          if (membershipId.includes('/verify/')) {
            membershipId = membershipId.split('/verify/').pop()
          }
          navigate(`/verify/${membershipId}`)
          return
        }
      }
      animRef.current = requestAnimationFrame(tick)
    }

    animRef.current = requestAnimationFrame(tick)
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current)
    }
  }, [scanning, navigate])

  // Cleanup on unmount
  useEffect(() => {
    return () => stopScan()
  }, [])

  const handleManualLookup = (e) => {
    e.preventDefault()
    if (!manualId.trim()) return
    navigate(`/verify/${manualId.trim()}`)
  }

  const sharedProps = {
    scanning,
    error,
    manualId,
    setManualId,
    startScan,
    stopScan,
    handleManualLookup,
    videoRef,
    canvasRef,
  }

  if (role === 'chapter_coordinator') {
    return <CoordinatorLayout><ScanContent {...sharedProps} /></CoordinatorLayout>
  }

  if (role === 'super_admin') {
    return <SuperAdminLayout><ScanContent {...sharedProps} /></SuperAdminLayout>
  }

  return <AdminLayout><ScanContent {...sharedProps} /></AdminLayout>
}